import type { ReactNode } from "react";
import { OnboardingProgress } from "@/features/legal/components/onboarding/OnboardingProgress";
import {
  ONBOARDING_COPY, ONBOARDING_SHOWCASE_TITLES, ONBOARDING_STEP_LABELS, type OnboardingStepKey,
} from "@/features/legal/config/onboarding.config";
import { buildOnboardingSteps } from "@/features/legal/utils/buildOnboardingSteps";
import { AuthShowcasePanel } from "@/shared/components/authShowcase/AuthShowcasePanel";
import { AUTH_SHOWCASE_CARD_CLASS } from "@/shared/components/authShowcase/authShowcase.style";

type PostLoginOnboardingLayoutProps = {
  currentStep: OnboardingStepKey
  hasSecurityStep: boolean
  hasLegalSteps: boolean
  captionSuffix?: string
  children: ReactNode
};

// Counts the login as step 1: the progress always starts from it.
const LOGIN_STEPS_COUNT = 1;

// Same card as the login page: the mascot panel on the left, the step on the right.
export const PostLoginOnboardingLayout = ({
  currentStep, hasSecurityStep, hasLegalSteps, captionSuffix, children,
}: PostLoginOnboardingLayoutProps) => {
  const steps = buildOnboardingSteps({ hasSecurityStep, hasLegalSteps });
  const position = steps.indexOf(currentStep) + 1 + LOGIN_STEPS_COUNT;
  const label = captionSuffix ?? ONBOARDING_STEP_LABELS[currentStep];
  const caption = hasLegalSteps
    ? ONBOARDING_COPY.STEP_CAPTION(position, steps.length + LOGIN_STEPS_COUNT, label)
    : ONBOARDING_COPY.STEP_CAPTION_WITHOUT_TOTAL(position, label);
  const showcaseTitle = ONBOARDING_SHOWCASE_TITLES[currentStep];

  return (
    <div className={AUTH_SHOWCASE_CARD_CLASS} data-testid="post-login-onboarding" data-step={currentStep}>
      <AuthShowcasePanel key={currentStep} titleLines={showcaseTitle.lines} titleAccent={showcaseTitle.accent} titleSize="step" />
      {/* The progress stays pinned to the top of the column; the step fills the rest. */}
      <section className="mx-auto flex w-full max-w-2xl flex-col gap-10 px-4 py-6 sm:px-10 lg:py-12">
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">{caption}</p>
          <OnboardingProgress steps={steps} currentStep={currentStep} />
        </div>
        <div key={currentStep} className="onboarding-enter flex flex-1 flex-col justify-center">{children}</div>
      </section>
    </div>
  );
};

PostLoginOnboardingLayout.displayName = "PostLoginOnboardingLayout";
